import React, { useState, useEffect } from 'react';
import { Briefcase, LogIn, UserPlus, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { login, register, hasUsers, getInviteInfo, getFeishuConfig, feishuLogin } from '../api';
import { User } from '../types';

interface Props {
  onLogin: (user: User, token: string) => void;
}

const ROLE_LABELS: Record<string, string> = { hr: 'HR', interviewer: '面试官' };

export default function LoginPage({ onLogin }: Props) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasExistingUsers, setHasExistingUsers] = useState(true);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteInfo, setInviteInfo] = useState<{ code: string; role: string; position_names: string[] } | null>(null);
  const [inviteError, setInviteError] = useState('');
  const [feishuEnabled, setFeishuEnabled] = useState(false);
  const [feishuAppId, setFeishuAppId] = useState('');
  const [feishuLoading, setFeishuLoading] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');

  // Check for invite code / feishu code in URL and whether users exist
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Handle Feishu OAuth callback
    const feishuCode = params.get('code');
    const state = params.get('state');
    if (feishuCode && state === 'feishu_oauth') {
      window.history.replaceState({}, '', window.location.pathname);
      setFeishuLoading(true);
      const redirectUri = window.location.origin + window.location.pathname;
      feishuLogin(feishuCode, redirectUri)
        .then(data => {
          localStorage.setItem('ats_token', data.token);
          localStorage.setItem('ats_user', JSON.stringify(data.user));
          onLogin(data.user, data.token);
          toast.success('飞书登录成功');
        })
        .catch(err => {
          if (err.response?.data?.pending) {
            setPendingMessage(err.response.data.message);
          } else {
            toast.error(err.response?.data?.message || '飞书登录失败');
          }
          setFeishuLoading(false);
        });
      return;
    }

    const code = params.get('invite');
    if (code) {
      setInviteCode(code);
      setMode('register');
      getInviteInfo(code)
        .then(info => setInviteInfo(info))
        .catch(err => setInviteError(err.response?.data?.message || '邀请码无效'));
    }
    hasUsers().then(data => {
      setHasExistingUsers(data.hasUsers);
      if (!data.hasUsers) setMode('register');
    }).catch(() => {});

    // Check if Feishu OAuth is enabled
    getFeishuConfig().then(cfg => {
      if (cfg.enabled && cfg.app_id) {
        setFeishuEnabled(true);
        setFeishuAppId(cfg.app_id);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canRegister = !hasExistingUsers || !!inviteCode;

  const handleFeishuLogin = () => {
    // Preserve deep-link params (e.g. ?candidate=xxx) across the Feishu OAuth redirect,
    // since redirect_uri only carries the pathname (query string is dropped).
    if (window.location.search) {
      sessionStorage.setItem('ats_pending_query', window.location.search);
    }
    const redirectUri = encodeURIComponent(window.location.origin + window.location.pathname);
    const authUrl = `https://accounts.feishu.cn/open-apis/authen/v1/authorize?app_id=${feishuAppId}&redirect_uri=${redirectUri}&state=feishu_oauth`;
    window.location.href = authUrl;
  };

  // Show loading screen while processing Feishu callback
  if (feishuLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-500">正在通过飞书登录...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error('请输入用户名和密码');
      return;
    }
    setLoading(true);
    try {
      let data;
      if (mode === 'login') {
        data = await login(username.trim(), password);
      } else {
        data = await register(username.trim(), password, displayName.trim() || undefined, inviteCode || undefined);
      }
      localStorage.setItem('ats_token', data.token);
      localStorage.setItem('ats_user', JSON.stringify(data.user));
      // Clean invite code from URL
      if (inviteCode) {
        window.history.replaceState({}, '', window.location.pathname);
      }
      onLogin(data.user, data.token);
      toast.success(mode === 'login' ? '登录成功' : '注册成功');
    } catch (err: any) {
      toast.error(err.response?.data?.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-600 mb-4 shadow-lg shadow-primary-200">
            <Briefcase size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">留形面试官</h1>
          <p className="text-sm text-gray-500 mt-1">招聘流程管理系统</p>
        </div>

        {/* Pending approval banner */}
        {pendingMessage && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm">
            <div className="flex items-center gap-2 text-amber-700 font-medium">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {pendingMessage}
            </div>
            <p className="mt-1.5 text-xs text-amber-600">您的飞书账号已关联成功，管理员激活后即可登录使用</p>
          </div>
        )}

        {/* Invite info banner */}
        {inviteCode && inviteInfo && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm">
            <div className="flex items-center gap-2 text-green-700 font-medium">
              <CheckCircle size={16} />你被邀请加入团队
            </div>
            <div className="mt-1.5 text-xs text-green-600 space-y-0.5">
              <p>角色: {ROLE_LABELS[inviteInfo.role] || inviteInfo.role}</p>
              {inviteInfo.position_names.length > 0 && (
                <p>可查看岗位: {inviteInfo.position_names.join('、')}</p>
              )}
            </div>
          </div>
        )}
        {inviteCode && inviteError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
            {inviteError}
          </div>
        )}

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 p-6">
          {/* Tab switch — only show if registration is possible */}
          {(canRegister || !hasExistingUsers) ? (
            <div className="flex mb-6 bg-gray-100 rounded-lg p-0.5">
              {hasExistingUsers && (
                <button
                  onClick={() => setMode('login')}
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                    mode === 'login' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                  }`}
                >
                  登录
                </button>
              )}
              <button
                onClick={() => setMode('register')}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === 'register' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                注册
              </button>
            </div>
          ) : (
            <h2 className="text-center text-base font-semibold text-gray-800 mb-5">登录</h2>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">用户名</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="请输入用户名"
                className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">密码</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="请输入密码"
                className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50"
              />
            </div>

            {mode === 'register' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">显示名称 (可选)</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="您的姓名或昵称"
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (mode === 'register' && !!inviteCode && !!inviteError)}
              className="w-full h-10 flex items-center justify-center gap-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : mode === 'login' ? (
                <><LogIn size={16} />登录</>
              ) : (
                <><UserPlus size={16} />注册</>
              )}
            </button>
          </form>

          {mode === 'register' && !hasExistingUsers && (
            <p className="text-[11px] text-gray-400 text-center mt-4">
              第一个注册的用户将成为管理员
            </p>
          )}

          {/* Feishu OAuth login */}
          {feishuEnabled && mode === 'login' && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">或</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <button
                type="button"
                onClick={handleFeishuLogin}
                className="w-full h-10 flex items-center justify-center gap-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 rounded-lg transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 5.5L9.5 14L14.5 7.5L21 18.5H3V5.5Z" fill="#3370FF"/>
                  <path d="M9.5 14L14.5 7.5L21 18.5H12L9.5 14Z" fill="#00D6B9"/>
                </svg>
                飞书账号登录
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
